import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { checkForHourlyReportDrafts, checkForIncidentReportDrafts } from '../utils/draftUtils'
import { postOrdersApi } from '../services/api'

export const useSmartNavigation = () => {
  const navigate = useNavigate()

  /**
   * Navigate to hourly report form, checking for existing drafts first
   */
  const navigateToHourlyReport = async () => {
    try {
      const draftInfo = await checkForHourlyReportDrafts()

      if (draftInfo.hasDraft) {
        toast.success(`Loading existing hourly report draft (${draftInfo.type})`)
        console.log(`📋 Loading existing ${draftInfo.type} hourly draft:`, draftInfo.draftId)
      } else {
        console.log('📋 No existing hourly drafts found, creating new report')
      }

      navigate('/hourly-report')
    } catch (error) {
      console.error('Error checking for hourly report drafts:', error)
      // Still navigate even if check fails
      navigate('/hourly-report')
    }
  }

  /**
   * Navigate to incident report form, checking for existing drafts first
   */
  const navigateToIncidentReport = async () => {
    try {
      const draftInfo = await checkForIncidentReportDrafts()

      if (draftInfo.hasDraft) {
        toast.success(`Loading existing incident report draft (${draftInfo.type})`)
        console.log(`📋 Loading existing ${draftInfo.type} incident draft:`, draftInfo.draftId)
      } else {
        console.log('📋 No existing incident drafts found, creating new report')
      }

      navigate('/incident-report')
    } catch (error) {
      console.error('Error checking for incident report drafts:', error)
      // Still navigate even if check fails
      navigate('/incident-report')
    }
  }

  /**
   * Navigate to reports based on type, checking for existing drafts
   */
  const navigateToReport = async (type: 'incident' | 'hourly') => {
    if (type === 'hourly') {
      await navigateToHourlyReport()
    } else {
      await navigateToIncidentReport()
    }
  }

  /**
   * Check for pending post orders and navigate intelligently
   */
  const checkAndNavigateToPostOrders = async (locationId: string, showSuccessMessage = true) => {
    try {
      
      const postOrdersResponse = await postOrdersApi.getPostOrdersByLocation(locationId)
      const locationPostOrders = postOrdersResponse.data || []

      // Filter for pending post orders that haven't been signed by this user
      const pendingPostOrders = locationPostOrders.filter((order: any) => {
        const isPending = order.status === 'PENDING'
        const isNotExpired = new Date(order.expiresAt) > new Date()
        const userHasSigned = order.signatures && order.signatures.length > 0

        return isPending && isNotExpired && !userHasSigned
      })

      if (pendingPostOrders.length > 0) {
        // Navigate to locations with post orders tab active
        setTimeout(() => {
          navigate('/locations', {
            state: {
              activeTab: 'postorders',
              pendingPostOrders: pendingPostOrders.length,
              locationId: locationId
            }
          })
        }, 500)

        return {
          hasPending: true,
          count: pendingPostOrders.length,
          orders: pendingPostOrders
        }
      } else {
        if (showSuccessMessage) {
          toast.success('Check-in successful! No pending post orders.', {
            duration: 3000,
            icon: '✅'
          })
        }
        
        return {
          hasPending: false,
          count: 0,
          orders: []
        }
      }
    } catch (error) {
      console.error('❌ Error checking post orders:', error)
      toast.error('Failed to check for pending post orders')
      
      return {
        hasPending: false,
        count: 0,
        orders: [],
        error: error
      }
    }
  }

  /**
   * Navigate to locations with specific tab active
   */
  const navigateToLocations = (activeTab?: string, additionalState?: any) => {
    navigate('/locations', {
      state: {
        activeTab,
        ...additionalState
      }
    })
  }

  /**
   * Navigate to post orders tab specifically
   */
  const navigateToPostOrders = (locationId?: string) => {
    navigateToLocations('postorders', { locationId })
  }

  return {
    navigateToHourlyReport,
    navigateToIncidentReport,
    navigateToReport,
    checkAndNavigateToPostOrders,
    navigateToLocations,
    navigateToPostOrders,
    // Still expose regular navigate for other uses
    navigate
  }
}

